import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { Dispatch, SetStateAction, useState } from 'react';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { AvailableFiltersSchema, PatientHPOListsSchema } from '@/zod_schemas';
import { AnnotationSource } from '@/api/annotationSource';
import { FiltersState } from '../types';
import FilterPanel from '../FilterPanel';

interface FilterLayoutProps {
  availableFilters: AvailableFiltersSchema;
  sourcesLoading: boolean;
  annotationSources: AnnotationSource | AnnotationSource[];
  setFinalFilters: Dispatch<SetStateAction<FiltersState>>;
  patientHpoList: PatientHPOListsSchema;
  setSelectedRows: Dispatch<SetStateAction<GridRowSelectionModel>>;
  setSelectedVariantRows: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedVariantRows: GridRowSelectionModel;
  refchPatientHPO: (options?: RefetchOptions | undefined) => Promise<
    QueryObserverResult<
      {
        hpo: {
          name: string;
          hpo_id: string;
          gene: string[];
        }[];
        patient_id: string;
      },
      Error
    >
  >;
}

const FilterLayout = ({
  availableFilters,
  sourcesLoading,
  annotationSources,
  setFinalFilters,
  patientHpoList,
  setSelectedRows,
  setSelectedVariantRows,
  selectedVariantRows,
  refchPatientHPO,
}: FilterLayoutProps) => {
  const [filterOpen, setFilterOpen] = useState(true);

  return (
    <Box
      sx={{
        width: filterOpen ? 450 : 80,
        transition: 'width 0.3s ease',
        borderRight: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Filter Header */}
      <Box
        onClick={() => setFilterOpen(!filterOpen)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: filterOpen ? 'space-between' : 'center',
          height: 50,
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        {filterOpen ? (
          <>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ whiteSpace: 'nowrap' }}
            >
              Filters
            </Typography>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setFilterOpen(false);
              }}
              size="small"
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
          </>
        ) : (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ fontSize: '0.9rem', whiteSpace: 'nowrap' }}
            >
              Filters
            </Typography>
            <ChevronRight fontSize="small" />
          </Stack>
        )}
      </Box>

      {/* Filter Content */}
      <Box
        sx={{
          opacity: filterOpen ? 1 : 0,
          pointerEvents: filterOpen ? 'auto' : 'none',
          transition: 'opacity 0.3s ease',
          px: 2,
          width: '100%',
          flexGrow: 1,
          overflowY: 'auto',
        }}
      >
        {filterOpen && (
          <FilterPanel
            availableFilters={availableFilters}
            sourcesLoading={sourcesLoading}
            annotationSources={annotationSources}
            setFinalFilters={setFinalFilters}
            patientHpoList={patientHpoList}
            setSelectedRows={setSelectedRows}
            setSelectedVariantRows={setSelectedVariantRows}
            selectedVariantRows={selectedVariantRows}
            refchPatientHPO={refchPatientHPO}
          />
        )}
      </Box>
    </Box>
  );
};

export default FilterLayout;
